import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ExternalLink } from 'lucide-react';

const SectionWrapper = styled.section`
    width: 100%;
    background: #fff;
`;

const SectionInner = styled.div`
    width: 100%;
    max-width: 1442px;
    margin: 0 auto;
    padding: 100px 80px;
    box-sizing: border-box;

    @media (max-width: 1360px) {
        padding: 88px 56px;
    }

    @media (max-width: 1180px) {
        padding: 72px 32px;
    }

    @media (max-width: 768px) {
        padding: 56px 20px;
        overflow: hidden;
    }

    @media (max-width: 480px) {
        padding: 48px 16px;
    }
`;

const Frame = styled.div`
    display: flex;
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    flex-direction: column;
    align-items: center;
    gap: 60px;

    @media (max-width: 768px) {
        gap: 40px;
    }
`;

const HeadingBlock = styled.div`
    display: flex;
    width: 842px;
    max-width: 100%;
    flex-direction: column;
    align-items: center;
    gap: 12px;
`;

const Eyebrow = styled.p`
    margin: 0;
    align-self: stretch;
    color: #6ea44c;
    text-align: center;
    font-family: 'Montserrat', sans-serif;
    font-size: 19.2px;
    font-style: normal;
    font-weight: 400;
    line-height: 180%;
    letter-spacing: 3.84px;
    text-transform: uppercase;

    @media (max-width: 768px) {
        font-size: 14px;
        letter-spacing: 2.8px;
    }
`;

const Heading = styled.h2`
    margin: 0;
    align-self: stretch;
    color: #003626;
    text-align: center;
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: 42px;

    @media (max-width: 768px) {
        font-size: 24px;
        line-height: 34px;
    }
`;

const DesktopCertificatesRow = styled.div`
    display: grid;
    width: 100%;
    align-self: stretch;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    column-gap: 20px;
    row-gap: 28px;

    @media (max-width: 1180px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 768px) {
        display: none;
    }
`;

const MobileCertificatesWrap = styled.div`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: center;
        gap: 24px;
    }
`;

const MobileViewport = styled.div`
    display: none;

    @media (max-width: 768px) {
        display: block;
        width: 100%;
        overflow: hidden;
    }
`;

const MobileTrack = styled.div`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        gap: 16px;
        will-change: transform;
    }
`;

const CertificateCard = styled.a`
    display: flex;
    width: 100%;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    text-decoration: none;

    @media (max-width: 768px) {
        width: 210px;
        min-width: 210px;
        flex-shrink: 0;
    }
`;

const CertificatePreview = styled.div`
    border-radius: 12px;
    background: #f8fbf6;
    display: flex;
    height: 236px;
    padding: 16px;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    align-self: stretch;
    overflow: hidden;
    box-shadow: inset 0 0 0 1px rgba(221, 237, 208, 0.7);

    iframe,
    object,
    embed {
        width: 100%;
        height: 100%;
        border: none;
        display: block;
        background: #fff;
        border-radius: 8px;
    }

    @media (max-width: 1180px) {
        height: 250px;
    }

    @media (max-width: 900px) {
        height: 220px;
        padding: 14px;
    }

    @media (max-width: 768px) {
        height: 162px;
        padding: 12px;
        border-radius: 10px;

        iframe,
        object,
        embed {
            border-radius: 6px;
        }
    }
`;

const CertificateInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    align-self: stretch;
`;

const CertificateTitle = styled.h3`
    margin: 0;
    color: #003626;
    font-family: 'Montserrat', sans-serif;
    font-size: 19.2px;
    font-style: normal;
    font-weight: 500;
    line-height: 27.2px;

    @media (max-width: 1180px) {
        font-size: 18px;
        line-height: 25px;
    }

    @media (max-width: 768px) {
        font-size: 14px;
        line-height: 21px;
    }
`;

const CertificateDate = styled.p`
    margin: 0;
    color: #6d716f;
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;

    @media (max-width: 768px) {
        font-size: 12px;
        line-height: 18px;
    }
`;

const ViewDocument = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #004d37;
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;

    svg {
        width: 12px;
        height: 12px;
        stroke-width: 2.3;
        flex-shrink: 0;
    }

    @media (max-width: 768px) {
        font-size: 13px;
    }
`;

const MobileControls = styled.div`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: 4px;
    }
`;

const MobileButton = styled.button`
    border: none;
    width: 104px;
    height: 36px;
    border-radius: 999px;
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
    cursor: pointer;
    transition: opacity 0.2s ease, background 0.2s ease;
    background: ${({ disabled }) => (disabled ? '#d9d9d9' : '#79b24c')};
    color: #fff;
    opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};

    &:disabled {
        cursor: not-allowed;
    }
`;

const Dots = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Dot = styled.button`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    border: none;
    padding: 0;
    cursor: pointer;
    background: ${({ active }) => (active ? '#79b24c' : '#cfe3b9')};
`;

const EmptyState = styled.div`
    width: 100%;
    text-align: center;
    color: #6d716f;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    line-height: 150%;
`;

const formatCertificateDate = value => {
    if (!value) return '';

    if (typeof value === 'string' && value.includes('-')) {
        const parts = value.split('-');

        if (parts.length === 3 && parts[0].length === 2) {
            const dd = parts[0];
            const mm = parts[1];
            const yyyy = parts[2];
            return `${dd}/${mm}/${yyyy.slice(-2)}`;
        }
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
        const dd = `${parsed.getDate()}`.padStart(2, '0');
        const mm = `${parsed.getMonth() + 1}`.padStart(2, '0');
        const yy = `${parsed.getFullYear()}`.slice(-2);
        return `${dd}/${mm}/${yy}`;
    }

    return value;
};

const buildPdfPreviewUrl = url => {
    if (!url) return '';
    return `${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
};

const PartnerCertificatesSection = ({
    heading = 'Environmental footprint management and Nature stewardship take dedication and time.',
    certificates = [],
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const mobileViewportRef = useRef(null);

    const visibleCertificates = useMemo(() => certificates.slice(0, 10), [certificates]);

    const maxMobileIndex = Math.max(visibleCertificates.length - 1, 0);

    const getCardStep = () => {
        return 226;
    };

    const handlePrev = () => {
        setActiveIndex(prev => Math.max(prev - 1, 0));
    };

    const handleNext = () => {
        setActiveIndex(prev => Math.min(prev + 1, maxMobileIndex));
    };

    const mobileTranslate = activeIndex * getCardStep();

    return (
        <SectionWrapper>
            <SectionInner>
                <Frame>
                    <HeadingBlock>
                        <Eyebrow>Commitment</Eyebrow>
                        <Heading>{heading}</Heading>
                    </HeadingBlock>

                    {visibleCertificates.length === 0 ? (
                        <EmptyState>No certificates available yet.</EmptyState>
                    ) : (
                        <>
                            <DesktopCertificatesRow>
                                {visibleCertificates.slice(0, 4).map(item => (
                                    <CertificateCard
                                        key={item.id}
                                        href={item.certificateUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <CertificatePreview>
                                            <iframe
                                                title={`Certificate ${item.id}`}
                                                src={buildPdfPreviewUrl(item.certificateUrl)}
                                            />
                                        </CertificatePreview>

                                        <CertificateInfo>
                                            <CertificateTitle>Partner Certificate</CertificateTitle>
                                            <CertificateDate>
                                                {formatCertificateDate(item.issuanceDate)}
                                            </CertificateDate>
                                            <ViewDocument>
                                                View document
                                                <ExternalLink />
                                            </ViewDocument>
                                        </CertificateInfo>
                                    </CertificateCard>
                                ))}
                            </DesktopCertificatesRow>

                            <MobileCertificatesWrap>
                                <MobileViewport ref={mobileViewportRef}>
                                    <MobileTrack
                                        style={{
                                            transform: `translateX(-${mobileTranslate}px)`,
                                            transition: 'transform 0.3s ease',
                                        }}
                                    >
                                        {visibleCertificates.map(item => (
                                            <CertificateCard
                                                key={item.id}
                                                href={item.certificateUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <CertificatePreview>
                                                    <iframe
                                                        title={`Certificate ${item.id}`}
                                                        src={buildPdfPreviewUrl(
                                                            item.certificateUrl
                                                        )}
                                                    />
                                                </CertificatePreview>

                                                <CertificateInfo>
                                                    <CertificateTitle>
                                                        Partner Certificate
                                                    </CertificateTitle>
                                                    <CertificateDate>
                                                        {formatCertificateDate(item.issuanceDate)}
                                                    </CertificateDate>
                                                    <ViewDocument>
                                                        View document
                                                        <ExternalLink />
                                                    </ViewDocument>
                                                </CertificateInfo>
                                            </CertificateCard>
                                        ))}
                                    </MobileTrack>
                                </MobileViewport>

                                <MobileControls>
                                    <MobileButton
                                        type="button"
                                        onClick={handlePrev}
                                        disabled={activeIndex === 0}
                                    >
                                        Back
                                    </MobileButton>

                                    <Dots>
                                        {visibleCertificates
                                            .slice(0, Math.min(3, visibleCertificates.length))
                                            .map((_, index) => (
                                                <Dot
                                                    key={index}
                                                    type="button"
                                                    active={index === Math.min(activeIndex, 2)}
                                                    onClick={() => setActiveIndex(index)}
                                                />
                                            ))}
                                    </Dots>

                                    <MobileButton
                                        type="button"
                                        onClick={handleNext}
                                        disabled={activeIndex === maxMobileIndex}
                                    >
                                        Next
                                    </MobileButton>
                                </MobileControls>
                            </MobileCertificatesWrap>
                        </>
                    )}
                </Frame>
            </SectionInner>
        </SectionWrapper>
    );
};

export default PartnerCertificatesSection;
